import { useCallback } from 'react';
import clsx from 'clsx';
import { toPlainText } from '@portabletext/toolkit';
import { SanityKeyed, ImageAndReviews as ImageAndReviewsProps } from 'types/studio/studio';

import {
  Button,
  CheckmarkIcon,
  Col,
  Container,
  RatingStars,
  Row,
  Space,
  SpanConfig,
  Typography
} from '@ag1/ui';

import { buttonClickEvent, clickPurchaseIntentEvent } from 'analytics/events/studio';

import { useStoreValue } from 'state/hooks/useStore';
import styles from './ImageAndReviews.module.scss';
import { StudioBaseImageV2 } from '../StudioBaseImage/StudioBaseImage';
import { CustomPortableText } from '../PortableText';
import { CommonSectionProps } from '../types';

const mediaColConfig = { xxs: 12, s: 6 } satisfies SpanConfig;
const infoColConfig = { xxs: 12, s: 6 } satisfies SpanConfig;

const ImageAndReviews = (props: SanityKeyed<ImageAndReviewsProps> & CommonSectionProps) => {
  const {
    _type: sectionType,
    bgColor,
    alignment,
    image,
    reviewItem: review,
    reviewDescription,
    addButton,
    cta,
    addCaption,
    caption,
    theme,
    visibleOnPageLoad
  } = props;

  const store = useStoreValue();

  const reverse = alignment === 'left';

  const handleCTAButtonClick = useCallback(() => {
    if (!cta) return;

    buttonClickEvent({
      location: sectionType,
      clickText: cta.portableText ? toPlainText(cta.portableText) : undefined,
      url: cta?.btnLink?.external || cta?.btnLink?.internal?.slug?.current
    });
    clickPurchaseIntentEvent({
      store,
      clickText: cta.portableText ? toPlainText(cta.portableText) : undefined,
      location: sectionType,
      url: cta?.btnLink?.external || cta?.btnLink?.internal?.slug?.current
    });
  }, [cta, sectionType, store]);

  return (
    <Space className={bgColor === 'stone' ? styles.bgStone : styles.bgWhite}>
      <Container className={styles.container} narrow>
        <Row className={clsx(styles.row, reverse && styles.reverse)} noGutter>
          <Col span={mediaColConfig} className={styles.mediaСol}>
            {image.image && (
              <StudioBaseImageV2
                image={image.image}
                mobileImage={image.mobileImage}
                withMobileImage={image.withMobileImage}
                tabletImage={image.tabletImage}
                className={styles.img}
                sizes={mediaColConfig}
                eagerLoading={visibleOnPageLoad}
              />
            )}
          </Col>
          <Col span={infoColConfig} className={styles.infoCol}>
            {review && (
              <div className={styles.review}>
                <RatingStars
                  rating={review.rating ?? 0}
                  className={styles.ratingStars}
                  childClassName={styles.padding}
                  inheritFontSize
                  thin
                  offset={4}
                />
                <Space top={{ xxs: 16, m: 24 }} bottom={{ xxs: 16, m: 24 }}>
                  <Typography
                    variant="headlineSans"
                    component="h3"
                    size="2"
                    className={styles.reviewText}
                    noPadding>
                    {review.text}
                  </Typography>
                </Space>
                <Typography variant="body" size="2" noPadding>
                  {'— '}
                  {review.author}
                </Typography>
                {reviewDescription && (
                  <Space top={4}>
                    <Typography variant="captionMono" size="1" color="grey-1">
                      {reviewDescription}
                    </Typography>
                  </Space>
                )}
              </div>
            )}
            <div className={styles.content}>
              {cta?.btnLink && addButton && (
                <Button
                  size="medium"
                  rounded
                  theme={theme}
                  icon={cta.showIcon && cta.iconOptions?.icon}
                  iconPosition={cta.iconOptions?.position}
                  className={styles.button}
                  href={cta.btnLink.external || cta.btnLink.internal?.slug?.current}
                  target={cta?.targetBlank ? '_blank' : '_self'}
                  data-gtm-skip-global
                  onClick={handleCTAButtonClick}>
                  {cta.portableText && <CustomPortableText value={cta.portableText} />}
                </Button>
              )}
              {addCaption && caption?.length > 0 && (
                <div className={styles.caption}>
                  {caption.map((item) => (
                    <div key={item} className={styles.captionItem}>
                      <Space right={2} bottom={2}>
                        <CheckmarkIcon size={12} />
                      </Space>
                      <Typography variant="portalCaption" size="1" color="black-1" noPadding>
                        {item}
                      </Typography>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </Space>
  );
};

export default ImageAndReviews;
